import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { isLocal } from '@/config/env';

type Factory<T> = () => Promise<{ default: T }>;

export interface LazyWithRetryOptions {
  /** Total attempts including the first call. Defaults: dev 7, prod 3. */
  retries?: number;
  /** Initial delay in ms; subsequent delays double (capped at 4000ms). Default 250. */
  baseDelay?: number;
  /** Hook for telemetry. Called on every failed attempt. */
  onError?: (err: unknown, attempt: number) => void;
}

// `ComponentType<any>` is intentional: federated/lazy modules cross a runtime
// boundary where compile-time prop typing isn't meaningful. Each lazy module's
// own .d.ts declares its real prop shape; this constraint just gates the
// factory's shape.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: Factory<T>,
  options: LazyWithRetryOptions = {},
): LazyExoticComponent<T> {
  const retries = options.retries ?? (isLocal ? 7 : 3);
  const baseDelay = options.baseDelay ?? 250;
  const onError = options.onError;

  return lazy(() => loadWithRetry(factory, retries, baseDelay, onError));
}

async function loadWithRetry<T>(
  factory: Factory<T>,
  retries: number,
  baseDelay: number,
  onError: ((err: unknown, attempt: number) => void) | undefined,
): Promise<{ default: T }> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await factory();
    } catch (err) {
      lastErr = err;
      onError?.(err, attempt);
      if (attempt === retries) break;
      // Exponential backoff with full jitter; cap to keep cold-start bounded.
      // dev budget @ retries=7, base=250: ~0.25 + 0.5 + 1 + 2 + 4 + 4 + 4 ≈ 15.75s
      // prd budget @ retries=3, base=250: ~0.25 + 0.5 ≈ 0.75s
      const cap = 4000;
      const exp = Math.min(cap, baseDelay * 2 ** (attempt - 1));
      const delay = Math.floor(Math.random() * exp);
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('lazyWithRetry: exhausted retries');
}
